import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Box,
    Typography,
    Button,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress,
    Chip,
    Divider,
    Stack,
    Snackbar,
    List,
    ListItem,
    ListItemText,
    Checkbox,
    FormControlLabel,
    FormGroup,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Archive as ArchiveIcon,
    Unarchive as UnarchiveIcon,
    Campaign as AnnouncementsIcon,
    Delete as DeleteIcon,
    DoneAll as DoneAllIcon,
    AttachFile as AttachFileIcon,
    OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import AnnouncementService from "../services/Announcement.service";
import { usePermissions } from "../hooks/usePermissions";

// ── Create / Edit Dialog ───────────────────────────────────────────────────────

const EMPTY_FORM = {
    title: "",
    memo: "",
    include_subcontractors: false,
    include_customers: false,
};

const AnnouncementDialog = ({ open, onClose, announcement, onSaved }) => {
    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [announcementData, setAnnouncementData] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const fileInputRef = useRef(null);
    const isEdit = Boolean(announcement);

    useEffect(() => {
        if (open) {
            if (announcement) {
                setAnnouncementData(announcement);
                setForm({
                    title: announcement.title,
                    memo: announcement.memo || "",
                    include_subcontractors:
                        announcement.include_subcontractors || false,
                    include_customers: announcement.include_customers || false,
                });
            } else {
                setAnnouncementData(null);
                setPendingAttachments((prev) => {
                    prev.forEach((a) => {
                        if (a.objectUrl) URL.revokeObjectURL(a.objectUrl);
                    });
                    return [];
                });
                setForm({ ...EMPTY_FORM });
            }
            setError(null);
        }
    }, [open, announcement]);

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            if (isEdit && announcementData) {
                const newIds = [];
                for (const file of files) {
                    const r = await AnnouncementService.uploadDocument(file);
                    newIds.push(r.data.id);
                }
                const existing = (
                    announcementData.attachments_detail || []
                ).map((d) => d.id);
                const res = await AnnouncementService.update(
                    announcementData.id,
                    { attachments: [...existing, ...newIds] },
                );
                setAnnouncementData(res.data);
            } else {
                for (const file of files) {
                    const objectUrl = URL.createObjectURL(file);
                    const r = await AnnouncementService.uploadDocument(file);
                    setPendingAttachments((prev) => [
                        ...prev,
                        { id: r.data.id, title: file.name, objectUrl },
                    ]);
                }
            }
        } catch {
            setError("Failed to upload attachment.");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const handleDeleteAttachment = async (docId) => {
        if (!isEdit) {
            setPendingAttachments((prev) => {
                const found = prev.find((a) => a.id === docId);
                if (found?.objectUrl) URL.revokeObjectURL(found.objectUrl);
                return prev.filter((a) => a.id !== docId);
            });
            try {
                await AnnouncementService.deleteDocument(docId);
            } catch {
                // best-effort cleanup
            }
            return;
        }
        if (!announcementData) return;
        try {
            const ids = (announcementData.attachments_detail || [])
                .filter((d) => d.id !== docId)
                .map((d) => d.id);
            const res = await AnnouncementService.update(
                announcementData.id,
                { attachments: ids },
            );
            await AnnouncementService.deleteDocument(docId);
            setAnnouncementData(res.data);
        } catch {
            setError("Failed to remove attachment.");
        }
    };

    const documents = isEdit
        ? announcementData?.attachments_detail || []
        : pendingAttachments;

    const handleSubmit = async () => {
        if (!form.title.trim()) {
            setError("Title is required.");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const payload = {
                ...form,
                ...(!isEdit && pendingAttachments.length > 0
                    ? { attachments: pendingAttachments.map((a) => a.id) }
                    : {}),
            };
            if (isEdit) {
                await AnnouncementService.update(announcement.id, payload);
            } else {
                await AnnouncementService.create(payload);
            }
            await onSaved();
            onClose();
        } catch (err) {
            const detail = err.response?.data;
            setError(
                typeof detail === "object"
                    ? Object.entries(detail)
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(" | ")
                    : detail || "An error occurred.",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle
                sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
            >
                {isEdit ? "Edit Announcement" : "New Announcement"}
            </DialogTitle>
            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={12}>
                        <TextField
                            label="Title *"
                            fullWidth
                            required
                            value={form.title}
                            onChange={(e) =>
                                setForm((p) => ({
                                    ...p,
                                    title: e.target.value,
                                }))
                            }
                            inputProps={{ maxLength: 100 }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Message"
                            fullWidth
                            multiline
                            rows={6}
                            value={form.memo}
                            onChange={(e) =>
                                setForm((p) => ({ ...p, memo: e.target.value }))
                            }
                            inputProps={{ maxLength: 2000 }}
                            helperText={`${form.memo.length} / 2000`}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography
                                variant="overline"
                                color="text.secondary"
                                sx={{ display: "block", mb: 0.5 }}
                            >
                                Audience
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mb: 1 }}
                            >
                                Always visible to employees and admins. Enable
                                below to also share with:
                            </Typography>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={form.include_subcontractors}
                                            onChange={(e) =>
                                                setForm((p) => ({
                                                    ...p,
                                                    include_subcontractors:
                                                        e.target.checked,
                                                }))
                                            }
                                        />
                                    }
                                    label="Subcontractors"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={form.include_customers}
                                            onChange={(e) =>
                                                setForm((p) => ({
                                                    ...p,
                                                    include_customers:
                                                        e.target.checked,
                                                }))
                                            }
                                        />
                                    }
                                    label="Customers (portal coming soon)"
                                />
                            </FormGroup>
                        </Paper>
                    </Grid>
                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                mb={1}
                            >
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                >
                                    Attachments
                                    {documents.length > 0
                                        ? ` (${documents.length})`
                                        : ""}
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={
                                        uploading ? (
                                            <CircularProgress size={12} />
                                        ) : (
                                            <AttachFileIcon />
                                        )
                                    }
                                    onClick={() =>
                                        fileInputRef.current?.click()
                                    }
                                    disabled={uploading}
                                >
                                    {uploading ? "Uploading…" : "Upload"}
                                </Button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    hidden
                                    onChange={handleFileChange}
                                />
                            </Stack>

                            {documents.length > 0 ? (
                                <List dense disablePadding>
                                    {documents.map((doc, idx) => (
                                        <ListItem
                                            key={doc.id}
                                            disableGutters
                                            divider={idx < documents.length - 1}
                                            secondaryAction={
                                                <Stack
                                                    direction="row"
                                                    spacing={0.5}
                                                >
                                                    {(doc.document_url ||
                                                        doc.objectUrl) && (
                                                        <Tooltip title="Open">
                                                            <IconButton
                                                                size="small"
                                                                component="a"
                                                                href={
                                                                    doc.document_url ||
                                                                    doc.objectUrl
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                <OpenInNewIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip title="Remove">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() =>
                                                                handleDeleteAttachment(
                                                                    doc.id,
                                                                )
                                                            }
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            }
                                        >
                                            <ListItemText
                                                primary={doc.title}
                                                primaryTypographyProps={{
                                                    variant: "body2",
                                                    noWrap: true,
                                                    sx: { pr: 9 },
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    No attachments yet.
                                </Typography>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={18} /> : null}
                >
                    {isEdit ? "Save" : "Post"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ── Archive Confirm Dialog ─────────────────────────────────────────────────────

const ArchiveDialog = ({ open, announcement, onClose, onArchived }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) setError(null);
    }, [open]);

    const handleArchive = async () => {
        setLoading(true);
        setError(null);
        try {
            await AnnouncementService.archive(announcement.id);
            await onArchived();
            onClose();
        } catch (err) {
            const detail = err.response?.data;
            setError(
                typeof detail === "object"
                    ? Object.values(detail).join(" ")
                    : detail || "Failed to archive announcement.",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Archive Announcement</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                        {error}
                    </Alert>
                )}
                <Typography>
                    Archive <strong>{announcement?.title}</strong>? It will be
                    hidden from employees but can be restored.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    color="warning"
                    variant="contained"
                    onClick={handleArchive}
                    disabled={loading}
                    startIcon={
                        loading ? (
                            <CircularProgress size={18} />
                        ) : (
                            <ArchiveIcon />
                        )
                    }
                >
                    Archive
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ── Restore Confirm Dialog ─────────────────────────────────────────────────────

const RestoreDialog = ({ open, announcement, onClose, onRestored }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) setError(null);
    }, [open]);

    const handleRestore = async () => {
        setLoading(true);
        setError(null);
        try {
            await AnnouncementService.unarchive(announcement.id);
            await onRestored();
            onClose();
        } catch (err) {
            const detail = err.response?.data;
            setError(
                typeof detail === "object"
                    ? Object.values(detail).join(" ")
                    : detail || "Failed to restore announcement.",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Restore Announcement</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                        {error}
                    </Alert>
                )}
                <Typography>
                    Restore <strong>{announcement?.title}</strong> so employees
                    can see it again?
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={handleRestore}
                    disabled={loading}
                    startIcon={
                        loading ? (
                            <CircularProgress size={18} />
                        ) : (
                            <UnarchiveIcon />
                        )
                    }
                >
                    Restore
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ── Delete Confirm Dialog ──────────────────────────────────────────────────────

const DeleteDialog = ({ open, announcement, onClose, onDeleted }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) setError(null);
    }, [open]);

    const handleDelete = async () => {
        setLoading(true);
        setError(null);
        try {
            await AnnouncementService.delete(announcement.id);
            await onDeleted();
            onClose();
        } catch (err) {
            const detail = err.response?.data;
            setError(
                typeof detail === "object"
                    ? Object.values(detail).join(" ")
                    : detail || "Failed to delete announcement.",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Delete Announcement</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                        {error}
                    </Alert>
                )}
                <Typography>
                    Permanently delete <strong>{announcement?.title}</strong>?
                    This cannot be undone.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    color="error"
                    variant="contained"
                    onClick={handleDelete}
                    disabled={loading}
                    startIcon={
                        loading ? (
                            <CircularProgress size={18} />
                        ) : (
                            <DeleteIcon />
                        )
                    }
                >
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ── Announcement Card ──────────────────────────────────────────────────────────

const AnnouncementCard = ({
    ann,
    canManage,
    showArchived,
    onEdit,
    onArchive,
    onRestore,
    onDelete,
}) => (
    <Paper
        elevation={0}
        sx={{
            border: "1px solid",
            borderColor: "primary.main",
            borderRadius: 1,
            p: 2.5,
        }}
    >
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                mb: 0.5,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                    flex: 1,
                    minWidth: 0,
                }}
            >
                <Typography
                    variant="h6"
                    fontWeight={ann.is_read ? 500 : 700}
                    sx={{
                        fontFamily: "Montserrat, sans-serif",
                        lineHeight: 1.3,
                    }}
                >
                    {ann.title}
                </Typography>
                {!ann.is_read && !showArchived && (
                    <Chip
                        label="NEW"
                        color="secondary"
                        size="small"
                        sx={{ fontWeight: 700, fontSize: "0.65rem" }}
                    />
                )}
            </Box>

            {canManage && (
                <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ ml: 1, flexShrink: 0 }}
                >
                    {!showArchived && (
                        <>
                            <Tooltip title="Edit">
                                <IconButton
                                    size="small"
                                    onClick={() => onEdit(ann)}
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Divider orientation="vertical" flexItem />
                            <Tooltip title="Archive">
                                <IconButton
                                    size="small"
                                    color="warning"
                                    onClick={() => onArchive(ann)}
                                >
                                    <ArchiveIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                    {showArchived && (
                        <Tooltip title="Restore">
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={() => onRestore(ann)}
                            >
                                <UnarchiveIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Divider orientation="vertical" flexItem />
                    <Tooltip title="Delete">
                        <IconButton
                            size="small"
                            color="error"
                            onClick={() => onDelete(ann)}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            )}
        </Box>

        <Box sx={{ display: "flex", gap: 2, mb: ann.memo ? 1 : 0 }}>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: "Montserrat, sans-serif" }}
            >
                {ann.created_by_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {new Date(ann.created).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                })}
            </Typography>
            {showArchived && ann.archived && (
                <Typography variant="caption" color="text.secondary">
                    Archived{" "}
                    {new Date(ann.archived).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                    })}
                </Typography>
            )}
            {canManage && ann.include_subcontractors && (
                <Chip label="Subcontractors" size="small" variant="outlined" />
            )}
            {canManage && ann.include_customers && (
                <Chip label="Customers" size="small" variant="outlined" />
            )}
        </Box>

        {ann.memo && (
            <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {ann.memo}
                </Typography>
            </>
        )}

        {ann.attachments_detail?.length > 0 && (
            <>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" flexWrap="wrap" gap={1}>
                    {ann.attachments_detail.map((doc) => (
                        <Chip
                            key={doc.id}
                            icon={<AttachFileIcon fontSize="small" />}
                            label={doc.title}
                            size="small"
                            variant="outlined"
                            clickable
                            component="a"
                            href={doc.document_url}
                            target="_blank"
                            rel="noopener noreferrer"
                        />
                    ))}
                </Stack>
            </>
        )}
    </Paper>
);

// ── Main Page ──────────────────────────────────────────────────────────────────

const Announcements = () => {
    const { isAdmin, isManagement } = usePermissions();
    const canManage = isAdmin || isManagement;

    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showArchived, setShowArchived] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [adminWarningOpen, setAdminWarningOpen] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = showArchived
                ? await AnnouncementService.listArchived()
                : await AnnouncementService.list();
            setAnnouncements(res.data.results ?? res.data);
        } catch {
            setError("Failed to load announcements.");
        } finally {
            setLoading(false);
        }
    }, [showArchived]);

    useEffect(() => {
        fetchData();
        AnnouncementService.markAllRead().catch(() => {});
        window.dispatchEvent(new Event("announcementsRead"));
    }, [fetchData]);

    const unreadCount = announcements.filter((a) => !a.is_read).length;

    const handleMarkAllRead = async () => {
        await AnnouncementService.markAllRead().catch(() => {});
        setAnnouncements((prev) => prev.map((a) => ({ ...a, is_read: true })));
        window.dispatchEvent(new Event("announcementsRead"));
    };

    return (
        <Box>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    border: "1px solid",
                    borderColor: "primary.main",
                    borderRadius: 1,
                    p: 2.5,
                    mb: 2,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 1,
                    }}
                >
                    <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                        <AnnouncementsIcon
                            sx={{ fontSize: 36, color: "secondary.main" }}
                        />
                        <Typography
                            variant="h5"
                            fontWeight={700}
                            sx={{ fontFamily: "Montserrat, sans-serif" }}
                        >
                            Announcements
                        </Typography>
                        {unreadCount > 0 && !showArchived && (
                            <Chip
                                label={`${unreadCount} new`}
                                color="secondary"
                                size="small"
                                sx={{ fontWeight: 700 }}
                            />
                        )}
                    </Box>

                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        {unreadCount > 0 && !showArchived && (
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DoneAllIcon />}
                                onClick={handleMarkAllRead}
                                sx={{
                                    fontFamily: "Montserrat, sans-serif",
                                    fontWeight: 600,
                                }}
                            >
                                Mark all read
                            </Button>
                        )}
                        {canManage && (
                            <Button
                                variant={
                                    showArchived ? "contained" : "outlined"
                                }
                                color="warning"
                                size="small"
                                startIcon={<ArchiveIcon />}
                                onClick={() => setShowArchived((v) => !v)}
                                sx={{
                                    fontFamily: "Montserrat, sans-serif",
                                    fontWeight: 600,
                                    width: 125,
                                }}
                            >
                                {showArchived ? "Active" : "Archived"}
                            </Button>
                        )}
                        {canManage && !showArchived && (
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    if (!isAdmin) {
                                        setAdminWarningOpen(true);
                                    } else {
                                        setSelected(null);
                                        setDialogOpen(true);
                                    }
                                }}
                                sx={{
                                    fontFamily: "Montserrat, sans-serif",
                                    fontWeight: 600,
                                    width: 125,
                                }}
                            >
                                New
                            </Button>
                        )}
                    </Stack>
                </Box>
            </Paper>

            {/* Error */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Content */}
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : announcements.length === 0 ? (
                <Paper
                    elevation={0}
                    sx={{
                        border: "1px solid",
                        borderColor: "primary.main",
                        borderRadius: 1,
                        p: 6,
                        textAlign: "center",
                    }}
                >
                    <AnnouncementsIcon
                        sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
                    />
                    <Typography color="text.secondary">
                        {showArchived
                            ? "No archived announcements."
                            : "No announcements yet."}
                    </Typography>
                </Paper>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {announcements.map((ann) => (
                        <AnnouncementCard
                            key={ann.id}
                            ann={ann}
                            canManage={canManage}
                            showArchived={showArchived}
                            onEdit={(a) => {
                                setSelected(a);
                                setDialogOpen(true);
                            }}
                            onArchive={(a) => {
                                setSelected(a);
                                setArchiveDialogOpen(true);
                            }}
                            onRestore={(a) => {
                                setSelected(a);
                                setRestoreDialogOpen(true);
                            }}
                            onDelete={(a) => {
                                setSelected(a);
                                setDeleteDialogOpen(true);
                            }}
                        />
                    ))}
                </Box>
            )}

            <AnnouncementDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                announcement={selected}
                onSaved={fetchData}
            />
            <ArchiveDialog
                open={archiveDialogOpen}
                onClose={() => setArchiveDialogOpen(false)}
                announcement={selected}
                onArchived={fetchData}
            />
            <RestoreDialog
                open={restoreDialogOpen}
                onClose={() => setRestoreDialogOpen(false)}
                announcement={selected}
                onRestored={fetchData}
            />
            <DeleteDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                announcement={selected}
                onDeleted={fetchData}
            />

            <Snackbar
                open={adminWarningOpen}
                autoHideDuration={4000}
                onClose={() => setAdminWarningOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity="warning"
                    onClose={() => setAdminWarningOpen(false)}
                    sx={{ width: "100%", fontFamily: "Montserrat, sans-serif" }}
                >
                    Only Admins can post announcements.
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Announcements;
